import * as Sentry from '@sentry/react';
import { call, put, select } from 'redux-saga/effects';
import { getOne, update } from 'api/cloudsave/cloudsaveApi';
import { AuthenticationError, NetworkError, TimeoutError } from 'api/apiClient';
import keycloak from 'auth/keycloak';
import {
  actionUndone,
  errorMessage,
  fileSavedMessage,
  makeACopyMessage,
  storageUnitMoved,
  storageUnitRenamed,
} from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import { setFileHasBeenEdited, setFileStatus } from 'store/fileState/reducer';
import { FileStatus } from 'store/fileState/types';
import type { RootState } from 'store';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { addFileEndingIfMissing } from 'util/file';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { requestConnectivityCheck } from 'util/connectivityChecks';
import {
  removeStorageUnitSuccess,
  setExplorerLoading,
  updateStorageUnitSuccess,
} from './reducer';
import type { UpdateStorageUnitRequestActionInterface } from './sagaActions';
import type {
  CurrentFile,
  StorageUnit,
  StorageUnitUpdate,
  StorageUnitsReducerInterface,
} from './types';
import { ROOT, StorageUnitType, UpdateActionOrigin } from './types';
import {
  hasRemoteStorageUnitContentChanged,
  mergeServerStorageUnitWithSubmittedContent,
  normalizeLeases,
} from './utils';
import { setCurrentFileFromStorageUnit } from './documentSagas';
import { findStorageUnitInState } from './storageUnitStateUtils';

const {
  DESIGNER,
  AUTOSAVE,
  RENAME_MODAL,
  MOVE_MODAL,
  DRAG_AND_DROP,
  UNDO,
  THUMBNAIL,
} = UpdateActionOrigin;

const designerSaveSequences = new Map<string, number>();

const usesDesignerSaveSequence = (origin: UpdateActionOrigin) =>
  origin === DESIGNER || origin === AUTOSAVE;

const nextDesignerSaveSequence = (origin: UpdateActionOrigin, id: string) => {
  if (!usesDesignerSaveSequence(origin)) {
    return 0;
  }

  const nextSequence = (designerSaveSequences.get(id) ?? 0) + 1;
  designerSaveSequences.set(id, nextSequence);
  return nextSequence;
};

const isStaleDesignerSaveSequence = (
  origin: UpdateActionOrigin,
  id: string,
  saveSequence: number,
) =>
  usesDesignerSaveSequence(origin) &&
  saveSequence !== designerSaveSequences.get(id);

function* finishStorageUnitUpdate({
  storageUnit,
  submittedUpdate,
  origin,
  oldUnit,
  isLocal,
}: {
  storageUnit: StorageUnit;
  submittedUpdate: StorageUnitUpdate;
  origin: UpdateActionOrigin;
  oldUnit?: StorageUnit;
  isLocal: boolean;
}): Generator<unknown, void, unknown> {
  if (origin === DESIGNER || origin === AUTOSAVE) {
    yield* setCurrentFileFromStorageUnit(storageUnit);
    yield put(setFileHasBeenEdited(false));
    if (origin === DESIGNER) {
      yield put(
        addMessage(fileSavedMessage(isLocal ? { isLocal } : undefined)),
      );
    }
    yield put(updateStorageUnitSuccess(storageUnit));
    return;
  }

  if (origin === RENAME_MODAL) {
    yield put(updateStorageUnitSuccess(storageUnit));
    if (oldUnit && (isLocal || oldUnit.name !== storageUnit.name)) {
      yield put(addMessage(storageUnitRenamed(storageUnit, oldUnit)));
    }
    return;
  }

  if (origin === MOVE_MODAL || origin === DRAG_AND_DROP) {
    yield put(removeStorageUnitSuccess(submittedUpdate.id));
    yield put(
      addMessage(
        storageUnitMoved(
          storageUnit,
          submittedUpdate.parentId,
          oldUnit ? (oldUnit.parentId ?? ROOT.id) : undefined,
        ),
      ),
    );
    return;
  }

  if (origin === UNDO) {
    yield put(addMessage(actionUndone()));
    yield put(updateStorageUnitSuccess(storageUnit));
    return;
  }

  if (origin === THUMBNAIL) {
    yield put(updateStorageUnitSuccess(storageUnit));
  }
}

function* updateStorageUnitOffline(
  storageUnit: StorageUnitUpdate,
  origin: UpdateActionOrigin,
  oldUnit?: StorageUnit,
  saveSequence = 0,
): Generator<unknown, void, unknown> {
  const now = new Date();
  const nowIso = now.toISOString();
  const existingPendingAction =
    oldUnit?.pendingAction === 'create' ? 'create' : null;
  const baseUnit: StorageUnit = oldUnit
    ? {
        ...oldUnit,
        softLeases: oldUnit.softLeases ?? [],
      }
    : {
        id: storageUnit.id,
        name: storageUnit.name ?? '',
        type: storageUnit.type,
        parentId: storageUnit.parentId,
        fileData: storageUnit.fileData,
        thumbnail: storageUnit.thumbnail,
        createdAt: now,
        updatedAt: now,
        exclusiveLease: undefined,
        softLeases: [],
        access: {
          direct: [],
          indirect: [],
        },
        status: [],
        archived: false,
      };

  const pendingAction =
    existingPendingAction === 'create' ? 'create' : 'update';
  const { projectMetadata, ...storageUnitFields } = storageUnit;

  const mergedUnit: StorageUnit = {
    ...baseUnit,
    ...storageUnitFields,
    ...(projectMetadata ? { project: projectMetadata } : {}),
    exclusiveLease: baseUnit.exclusiveLease ?? undefined,
    softLeases: baseUnit.softLeases ?? [],
    name:
      storageUnit.type === StorageUnitType.File && storageUnit.name
        ? addFileEndingIfMissing(storageUnit.name)
        : (storageUnit.name ?? baseUnit.name),
    updatedAt: now,
    isSynced: false,
    pendingAction,
    localUpdatedAt: nowIso,
  };

  if (isStaleDesignerSaveSequence(origin, storageUnit.id, saveSequence)) {
    return;
  }

  const normalized = (yield storageUnitCache.setOne(mergedUnit, {
    markSynced: false,
    pendingAction,
    localUpdatedAt: nowIso,
  })) as StorageUnit;
  yield storageUnitCache.updateUnitInLists(normalized);

  yield* finishStorageUnitUpdate({
    storageUnit: normalized,
    submittedUpdate: storageUnit,
    origin,
    oldUnit,
    isLocal: true,
  });
}

export function* updateStorageUnit(
  action: UpdateStorageUnitRequestActionInterface,
) {
  const { storageUnit, origin } = action.payload;
  const saveSequence = nextDesignerSaveSequence(origin, storageUnit.id);
  const isOnline: boolean = yield select(getIsOnline);
  const storageUnitsState: StorageUnitsReducerInterface = yield select(
    (state: RootState) => state.storageUnitsState,
  );
  const oldUnit = findStorageUnitInState(storageUnitsState, storageUnit.id);

  try {
    if (origin !== THUMBNAIL && origin !== AUTOSAVE) {
      yield put(setExplorerLoading(true));
    }

    if (!isOnline || !keycloak.token) {
      if (isStaleDesignerSaveSequence(origin, storageUnit.id, saveSequence)) {
        return;
      }
      yield* updateStorageUnitOffline(
        storageUnit,
        origin,
        oldUnit,
        saveSequence,
      );
      return;
    }

    if (origin === DESIGNER || origin === AUTOSAVE) {
      const currentFile: CurrentFile = yield select(
        (state: RootState) => state.storageUnitsState.currentFile,
      );
      const { parsedBody: serverVersion }: { parsedBody: StorageUnit } =
        yield withRetry(() => getOne(storageUnit.id));
      const cachedBaseline: StorageUnit | null = yield call(
        [storageUnitCache, storageUnitCache.getOne],
        storageUnit.id,
      );

      if (isStaleDesignerSaveSequence(origin, storageUnit.id, saveSequence)) {
        return;
      }

      if (
        currentFile.updatedAt &&
        serverVersion.updatedAt &&
        new Date(serverVersion.updatedAt).getTime() >
          new Date(currentFile.updatedAt).getTime() &&
        hasRemoteStorageUnitContentChanged(serverVersion, cachedBaseline)
      ) {
        yield put(setFileStatus(FileStatus.READ_ONLY));
        yield put(
          addMessage(
            makeACopyMessage(
              storageUnit.id,
              storageUnit.name ?? currentFile.name,
            ),
          ),
        );
        yield put(setExplorerLoading(false));
        return;
      }
    }

    const { parsedBody }: { parsedBody: StorageUnit } = yield withRetry(() =>
      update(storageUnit),
    );
    const storageUnitToCache = mergeServerStorageUnitWithSubmittedContent(
      parsedBody,
      storageUnit,
    );

    if (isStaleDesignerSaveSequence(origin, storageUnit.id, saveSequence)) {
      return;
    }

    const synced: StorageUnit =
      yield storageUnitCache.setOne(storageUnitToCache);
    yield storageUnitCache.clearList();
    const normalizedSynced = normalizeLeases(synced);

    yield* finishStorageUnitUpdate({
      storageUnit: normalizedSynced,
      submittedUpdate: storageUnit,
      origin,
      oldUnit,
      isLocal: false,
    });
  } catch (err) {
    if (
      err instanceof NetworkError ||
      err instanceof TimeoutError ||
      err instanceof AuthenticationError
    ) {
      if (isStaleDesignerSaveSequence(origin, storageUnit.id, saveSequence)) {
        return;
      }
      if (!(err instanceof AuthenticationError))
        yield call(requestConnectivityCheck);
      yield* updateStorageUnitOffline(
        storageUnit,
        origin,
        oldUnit,
        saveSequence,
      );
      return;
    }

    if (origin === AUTOSAVE) {
      Sentry.captureException(err, {
        tags: { saga: 'updateStorageUnit', origin: 'autosave' },
        extra: {
          storageUnitId: storageUnit.id,
          payloadSize: JSON.stringify(storageUnit).length,
          fileDataSize: storageUnit.fileData?.length ?? 0,
          hasFileData: !!storageUnit.fileData,
        },
      });
      yield put(addMessage(errorMessage('Autosave failed')));
    } else if (origin !== THUMBNAIL) {
      const payloadSize = JSON.stringify(storageUnit).length;
      const fileDataSize = storageUnit.fileData?.length ?? 0;

      Sentry.captureException(err, {
        tags: { saga: 'updateStorageUnit' },
        extra: {
          storageUnitId: storageUnit.id,
          origin,
          payloadSize,
          fileDataSize,
          hasFileData: !!storageUnit.fileData,
        },
      });
      yield put(
        addMessage(
          errorMessage(
            `Couldn't update ${storageUnit.type}. Please try again later.`,
          ),
        ),
      );
    } else {
      // NOTE: Thumbnail update failures are non-critical but worth tracking
      Sentry.captureException(err, {
        tags: { saga: 'updateStorageUnit', origin: 'thumbnail' },
        extra: { storageUnitId: storageUnit.id },
        level: 'warning',
      });
    }
  } finally {
    if (origin !== THUMBNAIL && origin !== AUTOSAVE) {
      yield put(setExplorerLoading(false));
    }
  }
}
